import { useState } from 'react';
import { useT, itemName } from '@/i18n';
import { getItem } from '@/domain/items';
import { upgradedStats } from '@/domain/upgrade';
import { SKILLS } from '@/domain/skills';
import { SET_NAMES } from '@/domain/itemSets';
import type { PlayerProfileData } from '@/state/leaderboardStore';
import type { SkillId, EquipmentSlot, EquipmentInstance } from '@/domain/types';
import { ItemIcon } from './ItemIcon';
import { PlayerAvatar } from './PlayerAvatar';
import { ItemDetailModal } from './ItemDetailModal';
import './PlayerProfileOverlay.css';

interface Props {
  profile: PlayerProfileData;
  onClose: () => void;
}

const SLOT_LABEL: Record<EquipmentSlot, string> = {
  weapon: '⚔', helmet: '⬡', armor: '◫', accessory: '◉', shield: '⛨',
  belt: '◐', sash: '∼', shoulder: '⌬',
};

const RARITY_COLOR: Record<string, string> = {
  common: '#a4b8c8', uncommon: '#4affb0', rare: '#4aa7ff',
  epic: '#c64aff', legendary: '#ff9a2b',
};

const VAULT_TIER_LABEL: Record<number, string> = {
  0: '—', 1: 'Alpha', 2: 'Sigma', 3: 'Tau', 4: 'Phi', 5: 'Omega',
};

function computeStats(profile: PlayerProfileData) {
  let atk = 5 + profile.level;
  let def = 2 + Math.floor(profile.level / 3);
  let hp  = 50 + (profile.level - 1) * 10;

  for (const eq of profile.equipment) {
    const def_ = getItem(eq.itemId);
    if (!def_?.stats) continue;
    const base = eq.rolledStats ?? def_.stats;
    const s = upgradedStats(base, eq.upgradeLevel, def_.stats);
    atk += s.attack;
    def += s.defense;
    hp  += s.health;
    if (eq.bonusStats) {
      for (const b of eq.bonusStats) {
        if (b.isMultiplier) continue;
        if (b.stat === 'attack') atk += b.value;
        else if (b.stat === 'defense') def += b.value;
        else if (b.stat === 'health') hp += b.value;
      }
    }
  }
  return { atk, def, hp };
}

export function PlayerProfileOverlay({ profile, onClose }: Props) {
  const stats = computeStats(profile);
  const t = useT();
  const [selectedItem, setSelectedItem] = useState<EquipmentInstance | null>(null);

  const slotOrder: EquipmentSlot[] = ['weapon', 'helmet', 'armor', 'accessory'];
  const equippedBySlot = Object.fromEntries(
    profile.equipment.map((eq) => [eq.slot, eq])
  ) as Partial<Record<EquipmentSlot, (typeof profile.equipment)[0]>>;

  const activeSkillDef  = profile.skillSlots[0] ? SKILLS[profile.skillSlots[0] as SkillId] : null;
  const passiveSkillDef = profile.skillSlots[1] ? SKILLS[profile.skillSlots[1] as SkillId] : null;

  const openItemDetail = (eq: (typeof profile.equipment)[0]) => {
    const inst: EquipmentInstance = {
      id: eq.id,
      itemId: eq.itemId,
      upgradeLevel: eq.upgradeLevel,
      rolledStats: eq.rolledStats,
      bonusStats: eq.bonusStats,
    };
    setSelectedItem(inst);
  };

  return (
    <>
    {selectedItem && (
      <ItemDetailModal
        instance={selectedItem}
        readOnly
        onClose={() => setSelectedItem(null)}
        onUpgrade={() => {}}
      />
    )}
    <div className="ppo__overlay" onClick={onClose}>
      <div className="ppo" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ppo__header">
          <div className="ppo__identity">
            <PlayerAvatar name={profile.name} avatarColor={profile.avatarColor ?? 'blue'} size="lg" />
            <div className="ppo__name">{profile.name}</div>
            <div className="ppo__badges">
              <span className="ppo__badge ppo__badge--level">Lv {profile.level}</span>
              {profile.highestVaultTier > 0 && (
                <span className="ppo__badge ppo__badge--vault">
                  {VAULT_TIER_LABEL[profile.highestVaultTier] ?? `T${profile.highestVaultTier}`}
                </span>
              )}
            </div>
          </div>
          <button className="ppo__close" onClick={onClose} aria-label="Schließen">✕</button>
        </div>

        {/* Combat stats */}
        <div className="ppo__stats">
          <div className="ppo__stat">
            <span className="ppo__stat-icon">⚔</span>
            <span className="ppo__stat-val">{stats.atk}</span>
            <span className="ppo__stat-lbl">ATK</span>
          </div>
          <div className="ppo__stat">
            <span className="ppo__stat-icon">🛡</span>
            <span className="ppo__stat-val">{stats.def}</span>
            <span className="ppo__stat-lbl">DEF</span>
          </div>
          <div className="ppo__stat">
            <span className="ppo__stat-icon">♥</span>
            <span className="ppo__stat-val">{stats.hp}</span>
            <span className="ppo__stat-lbl">HP</span>
          </div>
          <div className="ppo__stat ppo__stat--power">
            <span className="ppo__stat-icon">✦</span>
            <span className="ppo__stat-val">{profile.itemPower}</span>
            <span className="ppo__stat-lbl">POWER</span>
          </div>
        </div>

        {/* Equipment slots */}
        <div className="ppo__section-label">{t.character.equipment ?? 'Ausrüstung'}</div>
        <div className="ppo__equip-row">
          {slotOrder.map((slot) => {
            const eq = equippedBySlot[slot];
            const itemDef = eq ? getItem(eq.itemId) : null;
            const color = itemDef ? RARITY_COLOR[itemDef.rarity] : 'rgba(255,255,255,0.15)';
            return (
              <div
                key={slot}
                className={`ppo__equip ${eq ? 'ppo__equip--filled ppo__equip--clickable' : 'ppo__equip--empty'}`}
                style={{ '--slot-color': color } as React.CSSProperties}
                onClick={eq ? () => openItemDetail(eq) : undefined}
                role={eq ? 'button' : undefined}
              >
                <div className="ppo__equip-slot">{SLOT_LABEL[slot]}</div>
                {eq && itemDef ? (
                  <>
                    <ItemIcon itemId={itemDef.id} sizeRem={2} />
                    <div className="ppo__equip-name">
                      {itemName(itemDef.nameKey).slice(0, 14)}
                    </div>
                    <div className="ppo__equip-level" style={{ color }}>
                      +{eq.upgradeLevel}
                      {itemDef.setId && (
                        <span className="ppo__equip-set">◈</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="ppo__equip-empty">Leer</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Skills */}
        {(activeSkillDef || passiveSkillDef) && (
          <>
            <div className="ppo__section-label">Skills</div>
            <div className="ppo__skills">
              {[activeSkillDef, passiveSkillDef].map((skill, i) => {
                if (!skill) return (
                  <div key={i} className="ppo__skill ppo__skill--empty">—</div>
                );
                const skillName = (t as unknown as Record<string, Record<string, string>>).skills?.[skill.nameKey] ?? skill.nameKey;
                return (
                  <div key={i} className={`ppo__skill ppo__skill--${skill.type}`}>
                    <span className="ppo__skill-type">{skill.type === 'active' ? '⚡' : '🛡'}</span>
                    <span className="ppo__skill-name">{skillName}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Set bonuses shorthand */}
        {(() => {
          const setCounts: Partial<Record<string, number>> = {};
          for (const eq of profile.equipment) {
            const itemDef = getItem(eq.itemId);
            if (itemDef?.setId) setCounts[itemDef.setId] = (setCounts[itemDef.setId] ?? 0) + 1;
          }
          const activeSets = Object.entries(setCounts).filter(([, n]) => n! >= 2);
          if (activeSets.length === 0) return null;
          return (
            <>
              <div className="ppo__section-label">Sets</div>
              <div className="ppo__sets">
                {activeSets.map(([setId, n]) => (
                  <span key={setId} className="ppo__set-chip">
                    ◈ {SET_NAMES[setId as keyof typeof SET_NAMES]} {n}/4
                  </span>
                ))}
              </div>
            </>
          );
        })()}
      </div>
    </div>
    </>
  );
}
